import { computed, DestroyRef, inject, Injectable } from '@angular/core';

// Triggering rebuild to fix method resolution issues
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../user/user.service';
import { FeatureFlagService } from './feature-flag.service';
import { StoreRole } from '../store/store';
import {
   CustomRole,
   DEFAULT_LANDING_PAGE,
   DEFAULT_PERMISSIONS,
   LANDING_PAGES,
   REPORT_PERMISSION_IDS,
   RolePermission,
} from '../models/permission.model';
import {
   doc,
   docData,
   Firestore,
   Timestamp,
   updateDoc,
} from '@angular/fire/firestore';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { grantNewDefaultPermissions } from './new-default-permissions';

@Injectable({
   providedIn: 'root',
})
export class PermissionService {
   private userService = inject(UserService);
   private firestore = inject(Firestore);
   private snackBar = inject(MatSnackBar);
   private destroyRef = inject(DestroyRef);
   private featureFlagService = inject(FeatureFlagService);

   // Expose current user as a signal for reactive computed properties in components
   public currentUserSignal = inject(UserService).currentUserSignal;

   private rolePermissions$ = new BehaviorSubject<RolePermission[]>([]);
   private customRoles$ = new BehaviorSubject<CustomRole[]>([]);
   /** Landing page per role id (`settings.roleDefaultPages`). */
   private roleDefaultPages$ = new BehaviorSubject<Record<string, string>>({});
   private permissionsLoaded$ = new BehaviorSubject<boolean>(false);

   constructor() {
      this.userService.currentStore$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((store) => {
            this.permissionsLoaded$.next(false);
            if (store) {
               this.loadSettings(store.id);
            } else {
               this.rolePermissions$.next([]);
               this.customRoles$.next([]);
               this.roleDefaultPages$.next({});
            }
         });
   }

   checkPermissionWithNotification(
      permissionId: string,
      actionName?: string,
   ): boolean {
      const hasPermission = this.hasPermissionSync(permissionId);

      if (!hasPermission) {
         const message = actionName
            ? `You do not have permission to ${actionName}`
            : 'You do not have permission to perform this action';

         this.snackBar.open(message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar'],
         });
      }

      return hasPermission;
   }

   private loadSettings(storeId: string) {
      const settingsDoc = doc(this.firestore, `settings/${storeId}`);
      docData(settingsDoc)
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((settings: any) => {
            if (settings?.rolePermissions) {
               this.rolePermissions$.next(
                  grantNewDefaultPermissions(
                     this.expandLegacyReportPermissions(settings.rolePermissions),
                  ),
               );
            } else {
               this.rolePermissions$.next(this.getDefaultRolePermissions());
            }

            let customRoles = settings?.customRoles ?? [];
            const currentStore = this.userService.currentStore$.value;
            const hasKitchenUser = currentStore?.userRoles?.some((ur: any) => ur.role === 'Kitchen');

            if (hasKitchenUser && !customRoles.some((r: any) => r.id === 'Kitchen')) {
               customRoles = [
                  ...customRoles,
                  {
                     id: 'Kitchen',
                     name: 'Kitchen',
                     createdAt: Timestamp.now(),
                     createdBy: {
                        uid: 'system',
                        displayName: 'System',
                     },
                  },
               ];
            }

            this.customRoles$.next(customRoles);
            this.roleDefaultPages$.next(settings?.roleDefaultPages ?? {});
            this.permissionsLoaded$.next(true); // Mark as loaded
         });
   }

   /**
    * Backward-compat migration for the split of the single `view_reports`
    * permission into granular per-report permissions. Any role that only had
    * the legacy umbrella keeps full report access by gaining every granular
    * report permission. The umbrella is retained (it still gates non-report
    * features such as Bottle Keeping). The expansion is in-memory and is
    * persisted on the next save from the permission editor.
    */
   private expandLegacyReportPermissions(
      rolePerms: RolePermission[],
   ): RolePermission[] {
      if (!Array.isArray(rolePerms)) return rolePerms;
      return rolePerms.map((rp) => {
         const perms = rp.permissions ?? [];
         if (perms.includes('*') || !perms.includes('view_reports')) return rp;

         const missing = REPORT_PERMISSION_IDS.filter(
            (id) => !perms.includes(id),
         );
         if (missing.length === 0) return rp;

         return { ...rp, permissions: [...perms, ...missing] };
      });
   }

   private getDefaultRolePermissions(): RolePermission[] {
      return Object.entries(DEFAULT_PERMISSIONS).map(([role, permissions]) => ({
         role,
         permissions,
         isCustom: false,
         updatedBy: null,
      }));
   }

   // ─── Custom Roles ──────────────────────────────────────────────────────────

   getCustomRoles(): Observable<CustomRole[]> {
      return this.customRoles$.asObservable();
   }

   getCustomRolesSnapshot(): CustomRole[] {
      return this.customRoles$.value;
   }

   /**
    * Get the display name for any role (built-in or custom).
    */
   getRoleName(roleId: string): string {
      const custom = this.customRoles$.value.find((r) => r.id === roleId);
      return custom ? custom.name : roleId;
   }

   /**
    * Create a new custom role for the store.
    */
   async createCustomRole(storeId: string, name: string): Promise<CustomRole> {
      const currentUser = this.userService.currentUser$.value;
      if (!currentUser) throw new Error('No current user');

      const id = `custom_role_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

      const newRole: CustomRole = {
         id,
         name: name.trim(),
         createdAt: Timestamp.now(),
         createdBy: {
            uid: currentUser.id,
            displayName: currentUser.displayName,
         },
      };

      // Add the role to the store settings
      const updatedRoles = [...this.customRoles$.value, newRole];

      // Also create an empty RolePermission entry for it
      const updatedRolePerms = [
         ...this.rolePermissions$.value,
         {
            role: id,
            permissions: [],
            isCustom: true,
            updatedBy: {
               uid: currentUser.id,
               displayName: currentUser.displayName,
               timestamp: Timestamp.now(),
            },
         },
      ];

      await updateDoc(doc(this.firestore, `settings/${storeId}`), {
         customRoles: updatedRoles,
         rolePermissions: updatedRolePerms,
      });

      this.customRoles$.next(updatedRoles);
      this.rolePermissions$.next(updatedRolePerms);

      return newRole;
   }

   /**
    * Delete a custom role and remove it from all user-role assignments is done
    * outside this service (in StoreService). Here we clean up permissions.
    */
   async deleteCustomRole(storeId: string, roleId: string): Promise<void> {
      const updatedRoles = this.customRoles$.value.filter(
         (r) => r.id !== roleId,
      );
      const updatedRolePerms = this.rolePermissions$.value.filter(
         (rp) => rp.role !== roleId,
      );
      const { [roleId]: _removed, ...updatedDefaultPages } =
         this.roleDefaultPages$.value;

      await updateDoc(doc(this.firestore, `settings/${storeId}`), {
         customRoles: updatedRoles,
         rolePermissions: updatedRolePerms,
         roleDefaultPages: updatedDefaultPages,
      });

      this.customRoles$.next(updatedRoles);
      this.rolePermissions$.next(updatedRolePerms);
      this.roleDefaultPages$.next(updatedDefaultPages);
   }

   // ─── Permission Checks ─────────────────────────────────────────────────────

   hasPermission(permissionId: string): Observable<boolean> {
      return combineLatest([
         this.userService.currentUser$,
         this.permissionsLoaded$,
      ]).pipe(
         filter(([user, loaded]: [any, boolean]) => user !== null && loaded),
         map(([user]: [any, boolean]) => {
            if (!user || !user.storeRole) return false;
            if (user.storeRole === StoreRole.Owner) return true;

            // User-specific strict check for discount
            if (permissionId === 'make_discount') {
               return !!user.canMakeDiscount;
            }

            const rolePerms = this.rolePermissions$.value.find(
               (rp) => rp.role === user.storeRole,
            );
            if (!rolePerms) return false;
            if (rolePerms.permissions.includes('*')) return true;
            return rolePerms.permissions.includes(permissionId);
         }),
      );
   }

   hasPermissionSignal(permissionId: string) {
      return computed(() => {
         const user = this.currentUserSignal();
         if (!user || !user.storeRole) return false;
         if (user.storeRole === StoreRole.Owner) return true;

         // User-specific strict check for discount
         if (permissionId === 'make_discount') {
            return !!user.canMakeDiscount;
         }

         // For other permissions, we still rely on the loaded role permissions.
         // Since role permissions are a BehaviorSubject, we can't easily make them reactive in a signal
         // without toSignal, but role permissions change rarely compared to the user object.
         // For now, we'll keep using the sync check which reads the current value.
         return this.hasPermissionSync(permissionId);
      });
   }

   hasPermissionSync(permissionId: string): boolean {
      const user = this.userService.currentUser$.value;
      if (!user || !user.storeRole) return false;
      if (user.storeRole === StoreRole.Owner) return true;

      // User-specific strict check for discount
      if (permissionId === 'make_discount') {
         return !!user.canMakeDiscount;
      }

      const rolePerms = this.rolePermissions$.value.find(
         (rp) => rp.role === user.storeRole,
      );
      if (!rolePerms) return false;
      if (rolePerms.permissions.includes('*')) return true;
      return rolePerms.permissions.includes(permissionId);
   }

   getRolePermissions(): Observable<RolePermission[]> {
      return this.rolePermissions$.asObservable();
   }

   getRoleDefaultPages(): Observable<Record<string, string>> {
      return this.roleDefaultPages$.asObservable();
   }

   // ─── Landing Page ──────────────────────────────────────────────────────────

   /**
    * Where the current user should land after login / store selection: their
    * role's configured page, or the dashboard when none is set, the role lacks
    * the page's permission, or the store's add-on for it is off.
    */
   resolveLandingPage(): Observable<string> {
      return combineLatest([
         this.userService.currentUser$,
         this.permissionsLoaded$,
      ]).pipe(
         filter(([user, loaded]) => !!user?.storeId && loaded),
         take(1),
         map(([user]) => {
            const path = user?.storeRole
               ? this.roleDefaultPages$.value[user.storeRole]
               : undefined;
            const page = LANDING_PAGES.find((p) => p.path === path);
            if (!page || !user?.storeId) return DEFAULT_LANDING_PAGE;
            if (page.permission && !this.hasPermissionSync(page.permission)) {
               return DEFAULT_LANDING_PAGE;
            }
            if (
               page.featureKey &&
               !this.featureFlagService.isEnabledSync(page.featureKey, user.storeId)
            ) {
               return DEFAULT_LANDING_PAGE;
            }
            return page.path;
         }),
         // Never leave the root route hanging if settings fail to load.
         timeout({ first: 8000, with: () => of(DEFAULT_LANDING_PAGE) }),
      );
   }

   // ─── Role Permission Updates ────────────────────────────────────────────────

   async updateAllRolePermissions(
      storeId: string,
      rolePermissionsMap: Map<string, Set<string>>,
      roleDefaultPages?: Record<string, string>,
   ): Promise<void> {
      const currentUser = this.userService.currentUser$.value;
      if (!currentUser) throw new Error('No current user');

      const updatedRolePermissions: RolePermission[] = [];

      const allRoles = new Set<string>([
         ...this.rolePermissions$.value.map((rp) => rp.role),
         ...Array.from(rolePermissionsMap.keys()),
      ]);

      allRoles.forEach((role) => {
         const permissions = rolePermissionsMap.get(role);
         if (permissions) {
            updatedRolePermissions.push({
               role,
               permissions: Array.from(permissions),
               isCustom: role !== StoreRole.Owner,
               updatedBy:
                  role !== StoreRole.Owner
                     ? {
                          uid: currentUser.id,
                          displayName: currentUser.displayName,
                          timestamp: Timestamp.now(),
                       }
                     : null,
            });
         }
      });

      await updateDoc(doc(this.firestore, `settings/${storeId}`), {
         rolePermissions: updatedRolePermissions,
         ...(roleDefaultPages ? { roleDefaultPages } : {}),
      });
      this.rolePermissions$.next(updatedRolePermissions);
      if (roleDefaultPages) this.roleDefaultPages$.next(roleDefaultPages);
   }

   async updateRolePermissions(
      storeId: string,
      role: string,
      permissions: string[],
   ): Promise<void> {
      const currentUser = this.userService.currentUser$.value;
      if (!currentUser) throw new Error('No current user');

      const existingRoleIndex = this.rolePermissions$.value.findIndex(
         (rp) => rp.role === role,
      );
      let updatedRolePermissions: RolePermission[];

      if (existingRoleIndex >= 0) {
         updatedRolePermissions = this.rolePermissions$.value.map((rp) =>
            rp.role === role
               ? {
                    ...rp,
                    permissions,
                    isCustom: true,
                    updatedBy: {
                       uid: currentUser.id,
                       displayName: currentUser.displayName,
                       timestamp: Timestamp.now(),
                    },
                 }
               : rp,
         );
      } else {
         updatedRolePermissions = [
            ...this.rolePermissions$.value,
            {
               role,
               permissions,
               isCustom: true,
               updatedBy: {
                  uid: currentUser.id,
                  displayName: currentUser.displayName,
                  timestamp: Timestamp.now(),
               },
            },
         ];
      }

      await updateDoc(doc(this.firestore, `settings/${storeId}`), {
         rolePermissions: updatedRolePermissions,
      });
      this.rolePermissions$.next(updatedRolePermissions);
   }

   async resetToDefaults(storeId: string): Promise<void> {
      const defaultPerms = this.getDefaultRolePermissions();
      const customPerms = this.rolePermissions$.value.filter(
         (rp) => !Object.values(StoreRole).includes(rp.role as StoreRole)
      );
      const updatedPerms = [...defaultPerms, ...customPerms];
      await updateDoc(doc(this.firestore, `settings/${storeId}`), {
         rolePermissions: updatedPerms,
      });
      this.rolePermissions$.next(updatedPerms);
   }
}
